import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { APIURL } from '../../config';
import { 
    Box, 
    Card, 
    CardContent, 
    Typography, 
    Chip, 
    CircularProgress,
    Grid,
    Divider,
    Avatar
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import ContactsIcon from '@mui/icons-material/Contacts';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PeopleIcon from '@mui/icons-material/People';

const HouseholdMembers = ({ household_id, customerLabel = 'Customers', onMemberClick }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [members, setMembers] = useState([]);

    useEffect(() => {
        const fetchMembers = async () => {
            if (!household_id) return;

            setIsLoading(true);
            try {
                const response = await axios.get(`${APIURL}/households/members`, {
                    params: { household_id }
                });
                setMembers(response.data || []);
            } catch (error) {
                console.error('Error fetching household members:', error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchMembers();
    }, [household_id]);

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    };

    const getMemberName = (member) => {
        if (member.member_type === 'customer') {
            const name = (member.cus_name || '').trim();
            return name || `Unnamed ${customerLabel.replace(/s$/, '')}`;
        } else if (member.member_type === 'contact') {
            const firstName = (member.con_first_name || '').trim();
            const lastName = (member.con_last_name || '').trim();
            const fullName = `${firstName} ${lastName}`.trim();
            return fullName || 'Unnamed Contact';
        }
        return 'Unknown Member';
    };

    const getMemberEmail = (member) => {
        return member.member_type === 'customer' ? member.cus_email : member.con_email;
    };

    const getMemberPhone = (member) => {
        return member.member_type === 'customer' ? member.cus_phone : member.con_phone;
    };

    const getMemberTypeLabel = (member) => {
        if (member.member_type === 'customer') {
            return customerLabel.replace(/s$/, ''); // Singular form
        }
        return 'Contact';
    };

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (members.length === 0) {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <PeopleIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                    No Members Yet
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Add members to this household using the edit button above
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Household Members ({members.length})
                </Typography>
            </Box>

            <Grid container spacing={2}>
                {members.map((member) => (
                    <Grid item xs={12} sm={6} md={4} key={member.household_member_id}>
                        <Card
                            elevation={2}
                            onClick={() => onMemberClick?.(member)}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault();
                                    onMemberClick?.(member);
                                }
                            }}
                            role="link"
                            tabIndex={0}
                            sx={{
                                height: '100%',
                                cursor: 'pointer',
                                '&:hover': { boxShadow: 4 },
                                '&:focus-visible': { outline: '2px solid #257bcd', outlineOffset: 2 }
                            }}
                        >
                            <CardContent>
                                {/* Header with Avatar and Type */}
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <Avatar 
                                        sx={{ 
                                            bgcolor: member.member_type === 'customer' ? '#4caf50' : '#ff9800',
                                            width: 48,
                                            height: 48,
                                            mr: 2
                                        }}
                                    >
                                        {member.member_type === 'customer' ? 
                                            <PersonIcon /> : 
                                            <ContactsIcon />
                                        }
                                    </Avatar>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                                            {getMemberName(member)}
                                        </Typography>
                                        <Chip 
                                            label={getMemberTypeLabel(member)}
                                            size="small"
                                            color={member.member_type === 'customer' ? 'success' : 'warning'}
                                            sx={{ mt: 0.5 }}
                                        />
                                    </Box>
                                </Box>

                                <Divider sx={{ mb: 2 }} />

                                {/* Contact Information */}
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                    {getMemberEmail(member) && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <EmailIcon sx={{ fontSize: 18, color: '#757575' }} />
                                            <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                                                {getMemberEmail(member)}
                                            </Typography>
                                        </Box>
                                    )}
                                    
                                    {getMemberPhone(member) && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <PhoneIcon sx={{ fontSize: 18, color: '#757575' }} />
                                            <Typography variant="body2" color="text.secondary">
                                                {getMemberPhone(member)}
                                            </Typography>
                                        </Box>
                                    )}

                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <CalendarTodayIcon sx={{ fontSize: 18, color: '#757575' }} />
                                        <Typography variant="body2" color="text.secondary">
                                            Added: {formatDate(member.date_added)}
                                        </Typography>
                                    </Box>

                                    {member.last_contacted && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <CalendarTodayIcon sx={{ fontSize: 18, color: '#757575' }} />
                                            <Typography variant="body2" color="text.secondary">
                                                Last Contact: {formatDate(member.last_contacted)}
                                            </Typography>
                                        </Box>
                                    )}

                                    {(member.added_by_first_name || member.added_by_last_name) && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <PersonAddIcon sx={{ fontSize: 18, color: '#757575' }} />
                                            <Typography variant="body2" color="text.secondary">
                                                By: {member.added_by_first_name} {member.added_by_last_name}
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>
        </Box>
    );
};

export default HouseholdMembers;
